import type { TFunction } from "i18next";

export interface PersonaScriptMetaInput {
  slotIndex: number;
  sceneCount: number;
  duration: string | null;
}

export interface PersonaScriptVariantMeta {
  parentSlot: number;
  ordinal: number;
}

const SCRIPT_CREATED_AT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
};

const SCRIPT_CREATED_AT_SAME_YEAR_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
};

const MS_PER_SECOND = 1_000;

/** Each unit and how many of it make the next one, coarsest last. */
const SCRIPT_AGE_STEPS: ReadonlyArray<readonly [Intl.RelativeTimeFormatUnit, number]> = [
  ["second", 60],
  ["minute", 60],
  ["hour", 24],
  ["day", 30],
  ["month", 12],
  ["year", Number.POSITIVE_INFINITY],
];

/** Created date and time shown beside a script or brief meta line. Empty when the timestamp is invalid. */
export function formatScriptCreatedAt(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(locale, SCRIPT_CREATED_AT_OPTIONS);
}

/** Created date and time, without the year when it is the current one. Empty when the timestamp is invalid. */
export function formatScriptCreatedAtShort(iso: string, locale: string, now: number): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const isSameYear = date.getFullYear() === new Date(now).getFullYear();
  return date.toLocaleString(
    locale,
    isSameYear ? SCRIPT_CREATED_AT_SAME_YEAR_OPTIONS : SCRIPT_CREATED_AT_OPTIONS,
  );
}

/** How long ago a script or brief was created, e.g. `7 days ago`. Empty when the timestamp is invalid. */
export function formatScriptAge(iso: string, locale: string, now: number): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const format = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  let value = Math.trunc((then - now) / MS_PER_SECOND);
  for (const [unit, size] of SCRIPT_AGE_STEPS) {
    if (Math.abs(value) < size) return format.format(value, unit);
    value = Math.trunc(value / size);
  }
  return "";
}

/** `S2`, or `S2·H1` for a hook variant cut from slot 2. */
export function scriptSlotLabel(
  slotIndex: number,
  t: TFunction,
  variantMeta?: PersonaScriptVariantMeta | null,
): string {
  return variantMeta
    ? t("personaOpportunities.scripts.variantSlotLabel", {
        defaultValue: "S{{parent}}·H{{ordinal}}",
        parent: variantMeta.parentSlot,
        ordinal: variantMeta.ordinal,
      })
    : t("personaOpportunities.scripts.slotLabel", {
        defaultValue: "S{{slot}}",
        slot: slotIndex,
      });
}

/** Slot, scene count and duration, e.g. `S2 · 5 scenes · 30s`. */
export function scriptMetaLine(
  script: PersonaScriptMetaInput,
  t: TFunction,
  variantMeta?: PersonaScriptVariantMeta | null,
): string {
  const parts = [
    scriptSlotLabel(script.slotIndex, t, variantMeta),
    t("personaOpportunities.scripts.sceneCount", {
      defaultValue: "{{count}} scenes",
      defaultValue_one: "{{count}} scene",
      defaultValue_other: "{{count}} scenes",
      count: script.sceneCount,
    }),
  ];
  const duration = script.duration?.trim();
  if (duration) parts.push(duration);
  return parts.join(" · ");
}
